import type { PayloadAction } from '@reduxjs/toolkit'
import { createSlice } from '@reduxjs/toolkit'
import { map } from 'lodash'
import { useSelector } from 'react-redux'

import type { InAppNotificationResponse } from '~/data/api/client/workflows'
import type { PartialStore } from '~/store/types'

type NotificationOverlayStore = PartialStore<typeof notificationOverlaySlice>
type NotificationOverlayState = {
  overlayOpen: boolean
  overlayWorkflowNotifications: InAppNotificationResponse[]
}

const initialState: NotificationOverlayState = {
  overlayOpen: false,
  overlayWorkflowNotifications: []
}

const notificationOverlaySlice = createSlice({
  name: 'notificationOverlay',
  initialState,
  reducers: {
    setOverlayOpen: (state, action: PayloadAction<boolean>) => {
      state.overlayOpen = action.payload
      return state
    },
    // Replaces the held rows with the latest unread set so rows read elsewhere
    // drop out. The overlay opens only when a row it was not already holding
    // arrives.
    syncOverlayWorkflowNotifications: (state, action: PayloadAction<InAppNotificationResponse[]>) => {
      const heldIds = map(state.overlayWorkflowNotifications, 'id')
      const hasNewRows = action.payload.some(({ id }) => !heldIds.includes(id))
      state.overlayWorkflowNotifications = action.payload
      if (hasNewRows) state.overlayOpen = true
      if (state.overlayOpen && state.overlayWorkflowNotifications.length === 0) state.overlayOpen = false
      return state
    },
    clearOverlayNotifications: (state) => {
      state.overlayWorkflowNotifications = []
      state.overlayOpen = false
      return state
    },
    removeOverlayNotifications: (state, action: PayloadAction<string[]>) => {
      state.overlayWorkflowNotifications = state.overlayWorkflowNotifications.filter(
        ({ id }) => !action.payload.includes(id)
      )
      if (state.overlayOpen && state.overlayWorkflowNotifications.length === 0) {
        state.overlayOpen = false
      }
      return state
    }
  }
})

export const { setOverlayOpen, clearOverlayNotifications, removeOverlayNotifications, syncOverlayWorkflowNotifications } =
  notificationOverlaySlice.actions
export default notificationOverlaySlice.reducer

const selectOverlayOpen = (state: NotificationOverlayStore) => state.notificationOverlay.overlayOpen
const selectOverlayWorkflowNotifications = (state: NotificationOverlayStore) =>
  state.notificationOverlay.overlayWorkflowNotifications
export const useNotificationOverlayOpen = () => useSelector(selectOverlayOpen)
export const useOverlayWorkflowNotifications = () => useSelector(selectOverlayWorkflowNotifications)
